/**
 * Editor store layoutu (draft + zundo undo/redo). Oddělený od committed `useLayoutStore`.
 * Všechny strukturální editace přes čisté treeOps (immutable) + normalize. Save přes API.
 */

import { create } from "zustand";
import { temporal } from "zundo";
import {
  duplicateNode,
  insertNode,
  type LayoutDoc,
  type LayoutNode,
  type LayoutSize,
  makeGrid,
  moveNode,
  removeNode,
  uid,
  updateGridChild,
  updateNode,
  wrapInContainer,
} from "@globals/layouts";

type EditorState = {
  draftKey: string | null;
  draftName: string;
  doc: LayoutDoc | null;
  selectedId: string | null;
  dirty: boolean;
  /**
   * Verze, kterou mezitím uložil někdo jiný. `null` = čisto.
   *
   * Bez tohohle byl souběh horší než 409: `layouts:changed` přenačetl committed verzi
   * ve storu, `EditToolbar` z něj bral `version` pro uložení — a cizí změna se tím TIŠE
   * přepsala. Teď se rozdělaný draft nerebasuje, jen označí, a uložení se zamkne.
   */
  conflictVersion: number | null;

  beginEdit: (key: string, name: string, doc: LayoutDoc) => void;
  stop: () => void;
  /** Čistý draft se na cizí změnu přepíše tiše — není co ztratit. */
  rebase: (name: string, doc: LayoutDoc) => void;
  /** Rozdělaný draft se NEPŘEPISUJE, jen se označí konflikt. */
  noteExternalChange: (version: number) => void;
  select: (id: string | null) => void;

  /** Aplikuje transformaci na root (+ normalize + dirty). */
  apply: (fn: (root: LayoutNode) => LayoutNode) => void;
  patchNode: (id: string, patch: Partial<LayoutNode>) => void;
  setConfig: (id: string, key: string, value: unknown) => void;
  setSize: (id: string, size: LayoutSize) => void;
  addWidget: (parentId: string, type: string, defaultConfig: Record<string, unknown>, size?: LayoutSize) => void;
  addContainer: (parentId: string, dir: "row" | "col", wrap?: boolean) => void;
  addGrid: (parentId: string, cols: number) => void;
  setGridChild: (childId: string, patch: { colSpan?: number; rowStart?: number }) => void;
  remove: (id: string) => void;
  duplicate: (id: string) => void;
  wrap: (id: string, dir: "row" | "col") => void;
  reorder: (id: string, dir: -1 | 1) => void;
  move: (id: string, newParentId: string, index: number) => void;
};

const findParent = (root: LayoutNode, id: string): { parent: LayoutNode; index: number; siblings: LayoutNode[] } | null => {
  const kids = root.kind === "container" ? root.children : root.kind === "grid" ? root.children.map((c) => c.node) : root.kind === "tabs" ? root.tabs.map((t) => t.child) : [];
  for (let i = 0; i < kids.length; i++) {
    if (kids[i].id === id) return { parent: root, index: i, siblings: kids };
    const deep = findParent(kids[i], id);
    if (deep) return deep;
  }
  return null;
};

export const useLayoutEditor = create<EditorState>()(
  temporal(
    (set, get) => ({
      draftKey: null,
      conflictVersion: null,
      draftName: "",
      doc: null,
      selectedId: null,
      dirty: false,

      beginEdit: (key, name, doc) =>
        set({ draftKey: key, draftName: name, doc: structuredClone(doc), selectedId: null, dirty: false, conflictVersion: null }),
      stop: () => set({ draftKey: null, doc: null, selectedId: null, dirty: false, conflictVersion: null }),
      rebase: (name, doc) => set({ draftName: name, doc: structuredClone(doc), dirty: false, conflictVersion: null }),
      noteExternalChange: (version) => set({ conflictVersion: version }),
      select: (id) => set({ selectedId: id }),

      apply: (fn) => {
        const { doc } = get();
        if (!doc) return;
        // BEZ normalize během editace — jinak by se „zabalit" (1-dítě kontejner) hned rozbalil
        // a přidané prázdné řádky/sloupce/tabulky hned smazaly. Úklid až při uložení (EditToolbar).
        // Invariant „root je container" mutace drží samy (root se neodstraní; wrap(root) vrací container).
        const root = fn(doc.root);
        set({ doc: { ...doc, root } as LayoutDoc, dirty: true });
      },
      patchNode: (id, patch) => get().apply((root) => updateNode(root, id, patch)),
      setConfig: (id, key, value) =>
        get().apply((root) =>
          updateNode(root, id, ((): Partial<LayoutNode> => {
            const node = (function find(n: LayoutNode): LayoutNode | null {
              if (n.id === id) return n;
              const ks = n.kind === "container" ? n.children : n.kind === "grid" ? n.children.map((c) => c.node) : n.kind === "tabs" ? n.tabs.map((t) => t.child) : [];
              for (const k of ks) {
                const r = find(k);
                if (r) return r;
              }
              return null;
            })(root);
            const cfg = node && node.kind === "widget" ? node.config : {};
            return { config: { ...cfg, [key]: value } } as Partial<LayoutNode>;
          })())
        ),
      setSize: (id, size) => get().apply((root) => updateNode(root, id, { size } as Partial<LayoutNode>)),

      addWidget: (parentId, type, defaultConfig, size) => {
        const id = uid();
        get().apply((root) => insertNode(root, parentId, Number.MAX_SAFE_INTEGER, { kind: "widget", id, type, config: { ...defaultConfig }, ...(size ? { size } : {}) }));
        set({ selectedId: id });
      },
      addContainer: (parentId, dir, wrap = false) => {
        const id = uid();
        get().apply((root) =>
          insertNode(root, parentId, Number.MAX_SAFE_INTEGER, {
            kind: "container",
            id,
            dir,
            gap: 8,
            padding: 0,
            align: "stretch",
            justify: "start",
            wrap,
            children: [],
            size: { grow: 1, basis: "100%", minH: 60 },
          })
        );
        set({ selectedId: id });
      },
      addGrid: (parentId, cols) => {
        const g = makeGrid(cols);
        get().apply((root) => insertNode(root, parentId, Number.MAX_SAFE_INTEGER, g));
        set({ selectedId: g.id });
      },
      setGridChild: (childId, patch) => get().apply((root) => updateGridChild(root, childId, patch)),
      remove: (id) => {
        get().apply((root) => removeNode(root, id));
        if (get().selectedId === id) set({ selectedId: null });
      },
      duplicate: (id) => get().apply((root) => duplicateNode(root, id)),
      wrap: (id, dir) => get().apply((root) => wrapInContainer(root, id, dir)),
      reorder: (id, dir) => {
        const { doc } = get();
        if (!doc) return;
        const f = findParent(doc.root, id);
        if (!f) return;
        const newIndex = f.index + dir;
        if (newIndex < 0 || newIndex >= f.siblings.length) return;
        // moveNode = remove-then-insert (index v poli PO odebrání) → cílový index je přímo newIndex.
        get().apply((root) => moveNode(root, id, f.parent.id, newIndex));
      },
      move: (id, newParentId, index) => get().apply((root) => moveNode(root, id, newParentId, index)),
    }),
    { partialize: (s) => ({ doc: s.doc }), limit: 100 }
  )
);
